/**
 * Arabic script support for jsPDF: embedded Noto Sans Arabic + contextual shaping.
 */
import reshaper from 'arabic-persian-reshaper';
import { PDF_ARABIC_FONT_FAMILY, registerPdfArabicFont } from './fonts/notoSansArabic';

export { PDF_ARABIC_FONT_FAMILY };

const ARABIC_SCRIPT_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

export const containsArabicScript = (text) => ARABIC_SCRIPT_RE.test(String(text || ''));

const reshapeArabic = (text) => reshaper.ArabicShaper.convertArabic(String(text));

/** Apply Arabic presentation forms so letters connect in PDF. */
export const shapeArabicForPdf = (text) => {
    const raw = String(text ?? '');
    if (!containsArabicScript(raw)) return raw;
    try {
        return reshapeArabic(raw);
    } catch {
        return raw;
    }
};

/** Shape each line; leave pure-Latin lines unchanged. */
export const preparePdfArabicText = (text) => {
    const raw = String(text ?? '');
    if (!containsArabicScript(raw)) return raw;
    return raw
        .split('\n')
        .map((line) => (containsArabicScript(line) ? shapeArabicForPdf(line) : line))
        .join('\n');
};

export const ensurePdfArabicFont = (doc) => {
    registerPdfArabicFont(doc);
};

export const setPdfArabicFont = (doc) => {
    ensurePdfArabicFont(doc);
    doc.setFont(PDF_ARABIC_FONT_FAMILY, 'normal');
};

/**
 * Draw free-text (notes, treatment details) with Arabic font + RTL when needed.
 * @returns Y coordinate after the block
 */
export const writePdfFreeText = (doc, text, x, y, contentWidth, options = {}) => {
    const raw = String(text ?? '');
    if (!raw.trim()) return y;

    const gapAfter = options.gapAfter ?? 0;

    if (containsArabicScript(raw)) {
        ensurePdfArabicFont(doc);
        setPdfArabicFont(doc);
        const shaped = preparePdfArabicText(raw);
        const lines = doc.splitTextToSize(shaped, contentWidth);
        const rightX = x + contentWidth;
        doc.text(lines, rightX, y, { align: 'right', maxWidth: contentWidth });
        const height = doc.getTextDimensions(lines, { maxWidth: contentWidth }).h;
        return y + height + gapAfter;
    }

    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(raw, contentWidth);
    doc.text(lines, x, y, { maxWidth: contentWidth });
    const height = doc.getTextDimensions(lines, { maxWidth: contentWidth }).h;
    return y + height + gapAfter;
};

/**
 * Bold Helvetica label + body (Arabic-aware when body contains Arabic script).
 * @returns Y for the next section
 */
export const writePdfLabeledFreeText = (doc, label, text, x, y, contentWidth, options = {}) => {
    const labelGap = options.labelGap ?? 7;
    const sectionGap = options.sectionGap ?? 13;

    doc.setFont('helvetica', 'bold');
    doc.text(label, x, y);

    const bodyY = y + labelGap;
    const endY = writePdfFreeText(doc, text, x, bodyY, contentWidth, { gapAfter: 0 });
    return endY + sectionGap;
};
